import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Heart, Plus, Pencil, Trash2, Save, Eye, EyeOff } from 'lucide-react';
import { api } from '@/services/apiClient';

interface ModelSetting {
  id: number;
  provider_name: string;
  model_id: string;
  display_name: string;
  enabled: boolean;
  is_default: boolean;
  config: Record<string, any>;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface ProviderSetting {
  id: number;
  provider_name: string;
  display_name: string;
  enabled: boolean;
  is_default: boolean;
  config: Record<string, any>;
  models: ModelSetting[];
  created_at: string;
  updated_at: string;
}

interface VramSummary {
  total_vram_gb: number;
  used_vram_gb: number;
  models: Array<{ model_id: string; estimated_gb: number; enabled: boolean }>;
  warning: string | null;
}

interface Props {
  provider: ProviderSetting;
  vram: VramSummary | null;
  onUpdate: (name: string, update: Record<string, any>) => Promise<void>;
  onModelAdd: (providerName: string) => void;
  onModelEdit: (providerName: string, model: ModelSetting) => void;
  onModelDelete: (providerName: string, modelId: string) => void;
  healthCheckTrigger?: number;
  onHealthCheckDone?: () => void;
}

const ProviderSettingsCard: React.FC<Props> = ({
  provider, vram, onUpdate, onModelAdd, onModelEdit, onModelDelete,
  healthCheckTrigger = 0, onHealthCheckDone,
}) => {
  const [healthStatus, setHealthStatus] = useState<'idle' | 'checking' | 'healthy' | 'error'>('idle');
  const [healthError, setHealthError] = useState<string | null>(null);
  const [editingConfig, setEditingConfig] = useState(false);
  const [configDraft, setConfigDraft] = useState(provider.config);
  const [visibleKeys, setVisibleKeys] = useState<Record<string, boolean>>({});
  const [modelHealth, setModelHealth] = useState<Record<string, 'idle' | 'checking' | 'healthy' | 'error'>>({});
  const [modelHealthErrors, setModelHealthErrors] = useState<Record<string, string>>({});
  const prevTrigger = useRef(healthCheckTrigger);

  // 전체 상태 확인 트리거
  useEffect(() => {
    if (healthCheckTrigger > 0 && healthCheckTrigger !== prevTrigger.current) {
      prevTrigger.current = healthCheckTrigger;
      runFullHealthCheck();
    }
  }, [healthCheckTrigger]);

  const runFullHealthCheck = async () => {
    // Provider health check
    setHealthStatus('checking');
    setHealthError(null);
    const allModelIds = provider.models.map(m => m.model_id);
    // 모든 모델 checking 상태로
    const checkingState: Record<string, 'checking'> = {};
    allModelIds.forEach(id => { checkingState[id] = 'checking'; });
    setModelHealth(prev => ({ ...prev, ...checkingState }));

    // Provider + 모든 모델 병렬 실행
    const providerPromise = api.post(`/admin/ai/providers/settings/${provider.provider_name}/health`)
      .then(res => {
        setHealthStatus(res.data.healthy ? 'healthy' : 'error');
        if (!res.data.healthy) setHealthError(res.data.error);
      })
      .catch((err: any) => {
        setHealthStatus('error');
        setHealthError(err.response?.data?.detail || err.message);
      });

    const modelPromises = allModelIds.map(modelId =>
      api.post(`/admin/ai/providers/settings/${provider.provider_name}/models/${modelId}/health`)
        .then(res => {
          setModelHealth(prev => ({ ...prev, [modelId]: res.data.healthy ? 'healthy' : 'error' }));
          if (!res.data.healthy) setModelHealthErrors(prev => ({ ...prev, [modelId]: res.data.error }));
        })
        .catch((err: any) => {
          setModelHealth(prev => ({ ...prev, [modelId]: 'error' }));
          setModelHealthErrors(prev => ({ ...prev, [modelId]: err.response?.data?.detail || err.message }));
        })
    );

    await Promise.all([providerPromise, ...modelPromises]);
    onHealthCheckDone?.();
  };

  const handleHealthCheck = async () => {
    setHealthStatus('checking');
    setHealthError(null);
    try {
      const res = await api.post(`/admin/ai/providers/settings/${provider.provider_name}/health`);
      setHealthStatus(res.data.healthy ? 'healthy' : 'error');
      if (!res.data.healthy) setHealthError(res.data.error);
    } catch (err: any) {
      setHealthStatus('error');
      setHealthError(err.response?.data?.detail || err.message);
    }
  };

  const handleModelHealthCheck = async (modelId: string) => {
    setModelHealth(prev => ({ ...prev, [modelId]: 'checking' }));
    try {
      const res = await api.post(`/admin/ai/providers/settings/${provider.provider_name}/models/${modelId}/health`);
      setModelHealth(prev => ({ ...prev, [modelId]: res.data.healthy ? 'healthy' : 'error' }));
      if (!res.data.healthy) setModelHealthErrors(prev => ({ ...prev, [modelId]: res.data.error }));
    } catch (err: any) {
      setModelHealth(prev => ({ ...prev, [modelId]: 'error' }));
      setModelHealthErrors(prev => ({ ...prev, [modelId]: err.response?.data?.detail || err.message }));
    }
  };

  const handleToggleEnabled = () => {
    onUpdate(provider.provider_name, { enabled: !provider.enabled });
  };

  const handleSetDefault = () => {
    if (!provider.is_default) {
      onUpdate(provider.provider_name, { is_default: true });
    }
  };

  const handleSaveConfig = () => {
    // 빈 API key는 제거하여 환경변수 폴백 유지
    const cleaned = Object.fromEntries(
      Object.entries(configDraft).filter(([key, val]) => !(isSecretKey(key) && !val))
    );
    onUpdate(provider.provider_name, { config: cleaned });
    setEditingConfig(false);
  };

  const handleModelToggle = async (model: ModelSetting) => {
    try {
      await api.put(
        `/admin/ai/providers/settings/${provider.provider_name}/models/${model.model_id}`,
        { enabled: !model.enabled },
      );
      // 부모에 전체 Provider 업데이트 위임
      onUpdate(provider.provider_name, {});
    } catch {
      // 에러는 부모에서 처리
    }
  };

  const handleModelSetDefault = async (model: ModelSetting) => {
    try {
      await api.put(
        `/admin/ai/providers/settings/${provider.provider_name}/models/${model.model_id}`,
        { is_default: true },
      );
      onUpdate(provider.provider_name, {});
    } catch {
      // 에러는 부모에서 처리
    }
  };

  const getVramEstimate = (modelId: string): number | null => {
    if (!vram || provider.provider_name !== 'local') return null;
    const info = vram.models.find(m => m.model_id === modelId);
    return info?.estimated_gb ?? null;
  };

  const healthBadge = () => {
    switch (healthStatus) {
      case 'checking':
        return <Badge variant="info" className="text-xs animate-pulse">확인 중...</Badge>;
      case 'healthy':
        return <Badge variant="success" className="text-xs">연결됨</Badge>;
      case 'error':
        return <Badge variant="error" className="text-xs">오류</Badge>;
      default:
        return null;
    }
  };

  const isSecretKey = (key: string) => /api_key|secret|password/i.test(key);

  const maskValue = (value: string) => {
    if (!value) return '(미설정)';
    if (value.length <= 4) return '••••';
    return '••••••••' + value.slice(-4);
  };

  const toggleKeyVisibility = (key: string) => {
    setVisibleKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // external provider에서 편집 시작할 때 API key 필드가 없으면 추가
  const getConfigWithApiKeys = (config: Record<string, any>) => {
    if (provider.provider_name !== 'external') return config;
    const result = { ...config };
    if (!('openai_api_key' in result)) result.openai_api_key = '';
    if (!('anthropic_api_key' in result)) result.anthropic_api_key = '';
    if (!('google_api_key' in result)) result.google_api_key = '';
    return result;
  };

  // config 필드를 provider 유형에 따라 렌더링
  const renderConfigFields = () => {
    const config = editingConfig ? configDraft : provider.config;
    const displayConfig = editingConfig ? config : getConfigWithApiKeys(config);
    const entries = Object.entries(displayConfig);
    if (entries.length === 0) return <p className="text-xs text-gray-400">추가 설정 없음</p>;

    return (
      <div className="grid grid-cols-2 gap-3">
        {entries.map(([key, value]) => (
          <div key={key}>
            <Label className="text-xs text-gray-500">{key}</Label>
            {isSecretKey(key) ? (
              editingConfig ? (
                <div className="relative">
                  <Input
                    type={visibleKeys[key] ? 'text' : 'password'}
                    value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    placeholder="비워두면 환경변수 사용"
                    onChange={(e) => setConfigDraft(prev => ({ ...prev, [key]: e.target.value }))}
                    className="h-8 text-sm pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => toggleKeyVisibility(key)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {visibleKeys[key] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <p className="text-sm font-mono text-gray-700">
                    {String(value)
                      ? (visibleKeys[key] ? String(value) : maskValue(String(value)))
                      : '(환경변수)'
                    }
                  </p>
                  {String(value) && (
                    <button
                      type="button"
                      onClick={() => toggleKeyVisibility(key)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {visibleKeys[key] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
              )
            ) : (
              editingConfig ? (
                <Input
                  value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  onChange={(e) => {
                    let parsed: any = e.target.value;
                    try { parsed = JSON.parse(parsed); } catch {}
                    setConfigDraft(prev => ({ ...prev, [key]: parsed }));
                  }}
                  className="h-8 text-sm"
                />
              ) : (
                <p className="text-sm font-mono text-gray-700">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </p>
              )
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className={`${provider.enabled ? 'border-green-200' : 'border-gray-200'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg">{provider.display_name}</CardTitle>
            <Badge variant={provider.enabled ? 'success' : 'inactive'} className="text-xs">
              {provider.enabled ? '활성' : '비활성'}
            </Badge>
            {provider.is_default && (
              <Badge variant="warning" className="text-xs">기본</Badge>
            )}
            {healthBadge()}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleHealthCheck} disabled={healthStatus === 'checking'}>
              <Heart className="h-3 w-3 mr-1" />
              연결 확인
            </Button>
            <Button
              variant={provider.enabled ? 'outline' : 'default'}
              size="sm"
              onClick={handleToggleEnabled}
            >
              {provider.enabled ? '비활성화' : '활성화'}
            </Button>
            {!provider.is_default && provider.enabled && (
              <Button variant="outline" size="sm" onClick={handleSetDefault}>
                기본으로 설정
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Provider Config */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">Provider 설정</h4>
            {editingConfig ? (
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => { setEditingConfig(false); setConfigDraft(provider.config); }}>
                  취소
                </Button>
                <Button size="sm" onClick={handleSaveConfig}>
                  <Save className="h-3 w-3 mr-1" />
                  저장
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => { setEditingConfig(true); setConfigDraft(getConfigWithApiKeys(provider.config)); }}>
                <Pencil className="h-3 w-3 mr-1" />
                편집
              </Button>
            )}
          </div>
          {renderConfigFields()}
        </div>

        {/* Models Table */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">
              모델 ({provider.models.length})
            </h4>
            <Button variant="outline" size="sm" onClick={() => onModelAdd(provider.provider_name)}>
              <Plus className="h-3 w-3 mr-1" />
              모델 추가
            </Button>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">모델 ID</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">이름</th>
                  {provider.provider_name === 'local' && (
                    <th className="px-3 py-2 text-center font-medium text-gray-600">VRAM</th>
                  )}
                  {provider.provider_name === 'external' && (
                    <th className="px-3 py-2 text-center font-medium text-gray-600">API Key</th>
                  )}
                  <th className="px-3 py-2 text-center font-medium text-gray-600">상태</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600">활성</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600">기본</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {provider.models.map(model => (
                  <tr key={model.model_id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{model.model_id}</td>
                    <td className="px-3 py-2 text-gray-600">{model.display_name}</td>
                    {provider.provider_name === 'external' && (
                      <td className="px-3 py-2 text-center">
                        <Badge
                          variant={model.config?.api_key ? 'success' : 'default'}
                          className="text-xs"
                        >
                          {model.config?.api_key ? '개별 키' : '글로벌'}
                        </Badge>
                      </td>
                    )}
                    {provider.provider_name === 'local' && (
                      <td className="px-3 py-2 text-center">
                        {(() => {
                          const est = getVramEstimate(model.model_id);
                          if (est === null) return '-';
                          return (
                            <span className={`text-xs font-mono ${est > 8 ? 'text-red-600' : 'text-gray-600'}`}>
                              {est}GB
                            </span>
                          );
                        })()}
                      </td>
                    )}
                    <td className="px-3 py-2 text-center">
                      {(() => {
                        const status = modelHealth[model.model_id] || 'idle';
                        if (status === 'checking') return <span className="text-xs text-blue-500 animate-pulse">확인 중</span>;
                        if (status === 'healthy') return <Badge variant="success" className="text-xs">OK</Badge>;
                        if (status === 'error') return (
                          <span className="text-xs text-red-500" title={modelHealthErrors[model.model_id] || ''}>
                            오류
                          </span>
                        );
                        return (
                          <button
                            onClick={() => handleModelHealthCheck(model.model_id)}
                            className="text-xs text-gray-400 hover:text-blue-500"
                          >
                            <Heart className="h-3 w-3" />
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => handleModelToggle(model)}
                        className={`w-8 h-4 rounded-full transition-colors relative ${
                          model.enabled ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                            model.enabled ? 'left-4' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="radio"
                        name={`default-model-${provider.provider_name}`}
                        checked={model.is_default}
                        onChange={() => handleModelSetDefault(model)}
                        className="text-blue-600"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => onModelEdit(provider.provider_name, model)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => onModelDelete(provider.provider_name, model.model_id)}
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {provider.models.length === 0 && (
                  <tr>
                    <td colSpan={provider.provider_name === 'local' || provider.provider_name === 'external' ? 7 : 6} className="px-3 py-4 text-center text-gray-400">
                      등록된 모델이 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {healthError && (
          <p className="text-xs text-red-500 mt-1">연결 오류: {healthError}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default ProviderSettingsCard;
